import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const emptyForm = {
  name: '',
  location: '',
  review_url: '',
  notes: '',
};

export default function AdminAgencies() {
  useAuth(); // ensure authenticated
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Erro ao carregar agências');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (agency = null) => {
    if (agency) {
      setSelectedAgency(agency);
      setFormData({
        name: agency.name,
        location: agency.location || '',
        review_url: agency.review_url,
        notes: agency.notes || '',
      });
    } else {
      setSelectedAgency(null);
      setFormData(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.review_url.trim()) {
      toast.error('Nome e URL de reviews são obrigatórios');
      return;
    }

    setSubmitting(true);

    try {
      if (selectedAgency) {
        await axios.put(`${API}/agencies/${selectedAgency.id}`, formData);
        toast.success('Agência actualizada com sucesso');
      } else {
        await axios.post(`${API}/agencies`, formData);
        toast.success('Agência criada com sucesso');
      }
      setDialogOpen(false);
      fetchAgencies();
    } catch (error) {
      console.error('Error saving agency:', error);
      toast.error('Erro ao guardar agência');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (agency) => {
    try {
      await axios.put(`${API}/agencies/${agency.id}`, { active: !agency.active });
      toast.success(`Agência ${!agency.active ? 'activada' : 'desactivada'}`);
      fetchAgencies();
    } catch (error) {
      console.error('Error toggling agency:', error);
      toast.error('Erro ao alterar estado da agência');
    }
  };

  const handleDelete = async () => {
    if (!selectedAgency) return;

    try {
      await axios.delete(`${API}/agencies/${selectedAgency.id}`);
      toast.success('Agência eliminada com sucesso');
      setDeleteDialogOpen(false);
      setSelectedAgency(null);
      fetchAgencies();
    } catch (error) {
      console.error('Error deleting agency:', error);
      toast.error('Erro ao eliminar agência');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-agencies">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
            Agências
          </h1>
          <p className="text-gray-500 mt-1">Gerir agências e respectivos links de review</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-gold flex items-center gap-2"
          data-testid="add-agency-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nova Agência</span>
        </Button>
      </div>

      {agencies.length === 0 ? (
        <div className="bg-white border border-gray-100 p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhuma agência</h3>
          <p className="text-gray-400 mb-4">Comece por adicionar a primeira agência</p>
          <Button onClick={() => handleOpenDialog()} className="btn-gold">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Agência
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Localização</th>
                  <th>URL Reviews</th>
                  <th>Estado</th>
                  <th className="text-right">Acções</th>
                </tr>
              </thead>
              <tbody>
                {agencies.map((agency) => (
                  <tr key={agency.id} data-testid={`agency-row-${agency.id}`}>
                    <td className="font-medium text-[#212121]">{agency.name}</td>
                    <td className="text-gray-500">{agency.location || '-'}</td>
                    <td>
                      <a
                        href={agency.review_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#B5985A] hover:text-[#A3884A] flex items-center gap-1 text-sm"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Ver link
                      </a>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={agency.active}
                          onCheckedChange={() => handleToggleActive(agency)}
                          data-testid={`toggle-agency-${agency.id}`}
                        />
                        <span className={agency.active ? 'badge-active' : 'badge-inactive'}>
                          {agency.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(agency)}
                          className="hover:bg-[#B5985A]/10 hover:text-[#B5985A]"
                          data-testid={`edit-agency-${agency.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAgency(agency);
                            setDeleteDialogOpen(true);
                          }}
                          className="hover:bg-red-50 hover:text-red-500"
                          data-testid={`delete-agency-${agency.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="agency-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>
              {selectedAgency ? 'Editar Agência' : 'Nova Agência'}
            </DialogTitle>
            <p className="text-sm text-gray-500">
              {selectedAgency ? 'Actualize os dados da agência' : 'Preencha os dados para criar uma nova agência'}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-uppercase">Nome da Agência *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Century 21 Lisboa"
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agency-name-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Localização</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Ex: Lisboa, Portugal"
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agency-location-input"
              />
            </div>
            <div>
              <label className="label-uppercase">URL de Reviews (Google Maps) *</label>
              <Input
                value={formData.review_url}
                onChange={(e) => setFormData({ ...formData, review_url: e.target.value })}
                placeholder="https://g.page/r/..."
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                data-testid="agency-url-input"
              />
            </div>
            <div>
              <label className="label-uppercase">Notas Internas</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas opcionais..."
                className="border-2 border-gray-200 focus:border-[#B5985A] rounded-none resize-none"
                rows={3}
                data-testid="agency-notes-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="btn-outline"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="btn-gold"
                data-testid="save-agency-btn"
              >
                {submitting ? 'A guardar...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-agency-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Agência</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar a agência "{selectedAgency?.name}"?
              Esta acção é irreversível e os consultores associados serão desactivados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-agency"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
